import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AssociationWizard, {
  WizardStep
} from '../AssociationWizard/AssociationWizard';
import {
  BillingToggleSection,
  buildBillingLabels
} from '../shared/BillingToggleSection';
import { ConfirmAndRunStep, SummaryRow } from '../shared/ConfirmAndRunStep';
import { RuleViolation } from '../../helpers/types';
import { OverrideCredentialsStep } from './parts/OverrideCredentialsStep';
import {
  OverrideCredentials,
  initialOverrideCredentials,
  isOverrideCredentialsValid,
  trimOverrideCredentials
} from './helpers/overrideCredentials';

const WIZARD = 'tenantsAssociation.mspChildWizard';
const OVERRIDE = `${WIZARD}.override`;
const CONFIRM = `${WIZARD}.confirmStep`;
const OVERRIDE_CONFIRM = `${OVERRIDE}.confirmStep`;

export interface MspChildOverridePayload {
  credentials: OverrideCredentials;
}

export interface MspChildOverrideWizardProps {
  /** Upfront validation failures from the last rejected attempt. */
  upfrontErrors?: RuleViolation[];
  onBack: () => void;
  onFinish: (payload: MspChildOverridePayload) => void;
  isFinishing?: boolean;
  onStepChange?: (stepIndex: number) => void;
}

const MspChildOverrideWizard = ({
  upfrontErrors,
  onBack,
  onFinish,
  isFinishing,
  onStepChange
}: MspChildOverrideWizardProps) => {
  const { t } = useTranslation('activity');

  const [overrideCredentials, setOverrideCredentials] =
    useState<OverrideCredentials>(initialOverrideCredentials);
  const [uuidConfirmed, setUuidConfirmed] = useState(false);
  const [scheduledConfirmed, setScheduledConfirmed] = useState(false);

  const summaryRows = useMemo<SummaryRow[]>(
    () => [
      {
        label: t(`${CONFIRM}.SUMMARY_MODE`),
        value: t(`${OVERRIDE_CONFIRM}.MODE_VALUE`)
      },
      {
        label: t(`${OVERRIDE_CONFIRM}.SUMMARY_CHILD_UUID`),
        value: overrideCredentials.accountUuid
      },
      {
        label: t(`${CONFIRM}.SUMMARY_BILLING`),
        value: t(`${CONFIRM}.BILLING_MOVE_PAYG`)
      }
    ],
    [t, overrideCredentials.accountUuid]
  );

  // Unlike the create-under-parent list, this mode does not create a child
  // and disconnects from ChargeBee instead of switching the tenant type.
  const actions = useMemo(
    () => [
      t(`${CONFIRM}.ACTION_CLOUDINFRA_AUTH`),
      t(`${CONFIRM}.ACTION_ASSIGN_PAYG`),
      t(`${CONFIRM}.ACTION_ASSOCIATE_SASE`),
      t(`${OVERRIDE_CONFIRM}.ACTION_DISCONNECT_CHARGEBEE`),
      t(`${CONFIRM}.ACTION_UPDATE_SUBSCRIPTION`)
    ],
    [t]
  );

  const steps: WizardStep[] = useMemo(
    () => [
      {
        content: (
          <OverrideCredentialsStep
            values={overrideCredentials}
            onChange={setOverrideCredentials}
            uuidConfirmed={uuidConfirmed}
            onUuidConfirmedChange={setUuidConfirmed}
          >
            <BillingToggleSection labels={buildBillingLabels(t, OVERRIDE)} />
          </OverrideCredentialsStep>
        ),
        isValid:
          isOverrideCredentialsValid(overrideCredentials) && uuidConfirmed,
        subtitle: t(`${OVERRIDE}.credentialsStep.PAGE_SUBTITLE`)
      },
      {
        content: (
          <ConfirmAndRunStep
            scheduledConfirmed={scheduledConfirmed}
            onConfirmChange={setScheduledConfirmed}
            summaryRows={summaryRows}
            actions={actions}
            actionsTitle={t(`${CONFIRM}.ACTIONS_TITLE`)}
            confirmCheckboxLabel={t(`${CONFIRM}.CONFIRMATION_CHECKBOX`)}
            upfrontErrors={upfrontErrors}
            upfrontErrorsTitle={t(`${CONFIRM}.VALIDATION_FAILED_TITLE`)}
          />
        ),
        isValid: scheduledConfirmed,
        subtitle: t(`${OVERRIDE_CONFIRM}.PAGE_SUBTITLE`)
      }
    ],
    [
      t,
      overrideCredentials,
      uuidConfirmed,
      scheduledConfirmed,
      summaryRows,
      actions,
      upfrontErrors
    ]
  );

  const handleFinish = useCallback(
    () =>
      onFinish({
        credentials: trimOverrideCredentials(overrideCredentials)
      }),
    [onFinish, overrideCredentials]
  );

  return (
    <AssociationWizard
      steps={steps}
      cancelLabel={t('buttons.CANCEL')}
      previousLabel={t(`${WIZARD}.PREVIOUS`)}
      nextLabel={t(`${OVERRIDE}.NEXT`)}
      finishLabel={t(`${OVERRIDE}.FINISH`)}
      onBack={onBack}
      onFinish={handleFinish}
      isFinishing={isFinishing}
      onStepChange={onStepChange}
    />
  );
};

export default MspChildOverrideWizard;
